// src/components/UpdateMatchData.js
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { FiSearch, FiDownloadCloud, FiRefreshCw } from 'react-icons/fi';
import tournamentService from '../services/tournamentService';
import adminService from '../services/adminService';
import '../styles/AdminDashboard.css';

const formatMatchLabel = (m) => {
    const home = m.homeTeam?.name || '?';
    const away = m.awayTeam?.name || '?';
    const date = m.date ? new Date(m.date).toLocaleString() : '—';
    return `${home} – ${away} (${date})`;
};

const scoreOrDash = (h, a) =>
    (h === null || h === undefined || a === null || a === undefined) ? '—' : `${h} : ${a}`;

const UpdateMatchData = () => {
    const [tournaments, setTournaments] = useState([]);
    const [allMatches, setAllMatches] = useState([]);

    const [tournamentId, setTournamentId] = useState('');
    const [round, setRound] = useState('');
    const [matchId, setMatchId] = useState('');

    const [updating, setUpdating] = useState(false);
    const [result, setResult] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [tournamentData, matchData] = await Promise.all([
                    tournamentService.getAllTournaments(),
                    adminService.getEditableMatches(),
                ]);
                const sorted = (Array.isArray(tournamentData) ? tournamentData : [])
                    .slice()
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setTournaments(sorted);
                // Update data z API-Football je jen pro fotbal, s vyplněným apiFootballId.
                const footballMatches = (Array.isArray(matchData) ? matchData : [])
                    .filter(m => (m.sportType || '').toUpperCase() === 'FOOTBALL' && m.apiFootballId != null);
                setAllMatches(footballMatches);
            } catch (e) {
                console.error('Failed to load data for update match data page', e);
                toast.error('Nepodařilo se načíst turnaje a zápasy');
            }
        };
        fetchData();
    }, []);

    // Turnaje, které mají alespoň jeden fotbalový zápas s apiFootballId
    const selectableTournamentIds = new Set(
        allMatches.flatMap(m => (m.tournaments || []).map(t => String(t.id)))
    );
    const visibleTournaments = tournaments.filter(t => selectableTournamentIds.has(String(t.id)));

    const tournamentMatches = tournamentId
        ? allMatches.filter(m => (m.tournaments || []).some(t => String(t.id) === String(tournamentId)))
        : [];

    const rounds = Array.from(new Set(tournamentMatches.map(m => m.round || '—')))
        .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    const matchesInRound = round
        ? tournamentMatches.filter(m => (m.round || '—') === round)
        : [];

    const selectedMatch = matchId
        ? allMatches.find(m => String(m.id) === String(matchId))
        : null;

    const handleTournamentChange = (e) => {
        setTournamentId(e.target.value);
        setRound('');
        setMatchId('');
        setResult(null);
    };

    const handleRoundChange = (e) => {
        setRound(e.target.value);
        setMatchId('');
        setResult(null);
    };

    const handleMatchChange = (e) => {
        setMatchId(e.target.value);
        setResult(null);
    };

    const handleUpdateData = async () => {
        const id = String(matchId).trim();
        if (!id || Number.isNaN(Number(id))) {
            toast.error('Vyberte zápas');
            return;
        }
        setUpdating(true);
        try {
            const data = await adminService.updateMatchDataFromApi(id);
            setResult(data);
            toast.success(`Data zápasu aktualizována: ${data.matchStatus} ${scoreOrDash(data.scoreHome, data.scoreAway)}`);
        } catch (error) {
            console.error('Failed to update match data', error);
            const detail = error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se aktualizovat data zápasu: ${detail}`);
        } finally {
            setUpdating(false);
        }
    };

    return (
        <div className="admin-dashboard-container">
            <h2 className="admin-section-title">Aktualizace dat zápasu</h2>

            <div className="admin-edit-stack">
                {/* Karta: Vyhledání zápasu */}
                <div className="admin-action-card">
                    <div className="admin-action-header">
                        <span className="admin-action-icon"><FiSearch /></span>
                        <h3>Vyhledat zápas</h3>
                    </div>
                    <p className="admin-action-desc">
                        V nabídce jsou fotbalové zápasy navázané na turnaj (odehrané v posledním měsíci nebo
                        naplánované do budoucna) s vyplněným API-Football ID. Vyber zápas přes turnaj, kolo
                        a konkrétní zápas a pak klikni na „Update data".
                    </p>
                    <div className="admin-action-body">
                        <div className="admin-field">
                            <label>Turnaj</label>
                            <select
                                className="admin-select"
                                value={tournamentId}
                                onChange={handleTournamentChange}
                                disabled={visibleTournaments.length === 0}
                            >
                                <option value="">— Vyberte turnaj —</option>
                                {visibleTournaments.map(t => (
                                    <option key={t.id} value={t.id}>
                                        {t.name}{t.year ? ` (${t.year})` : ''} [{t.sportType}]
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label>Kolo</label>
                            <select
                                className="admin-select"
                                value={round}
                                onChange={handleRoundChange}
                                disabled={!tournamentId || rounds.length === 0}
                            >
                                {!tournamentId ? (
                                    <option value="">— Nejdřív turnaj —</option>
                                ) : rounds.length === 0 ? (
                                    <option value="">Žádné zápasy</option>
                                ) : (
                                    <>
                                        <option value="">— Vyberte kolo —</option>
                                        {rounds.map(r => (
                                            <option key={r} value={r}>{r === '—' ? 'Bez kola' : r}</option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>
                        <div className="admin-field">
                            <label>Zápas</label>
                            <select
                                className="admin-select"
                                value={matchId}
                                onChange={handleMatchChange}
                                disabled={!round || matchesInRound.length === 0}
                            >
                                {!round ? (
                                    <option value="">— Nejdřív kolo —</option>
                                ) : matchesInRound.length === 0 ? (
                                    <option value="">Žádné zápasy v kole</option>
                                ) : (
                                    <>
                                        <option value="">— Vyberte zápas —</option>
                                        {matchesInRound.map(m => (
                                            <option key={m.id} value={m.id}>
                                                {formatMatchLabel(m)}
                                            </option>
                                        ))}
                                    </>
                                )}
                            </select>
                        </div>
                    </div>

                    {selectedMatch && (
                        <div className="admin-action-body">
                            <div className="admin-field">
                                <label>Football-API ID</label>
                                <input
                                    type="text"
                                    className="admin-input"
                                    value={selectedMatch.apiFootballId ?? '—'}
                                    readOnly
                                    disabled
                                />
                            </div>
                        </div>
                    )}

                    <button
                        className="admin-action-btn"
                        onClick={handleUpdateData}
                        disabled={updating || !matchId}
                    >
                        {updating ? 'Stahuji…' : <><FiDownloadCloud /> Update data</>}
                    </button>
                </div>

                {/* Karta: Výsledek aktualizace */}
                {result && (
                    <div className="admin-action-card">
                        <div className="admin-action-header">
                            <span className="admin-action-icon"><FiRefreshCw /></span>
                            <h3>Výsledek aktualizace</h3>
                        </div>
                        {selectedMatch && (
                            <p className="admin-action-desc">{formatMatchLabel(selectedMatch)}</p>
                        )}

                        <div className="admin-score-section">
                            <span className="admin-field-group-title">Stav a skóre</span>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Stav zápasu</span>
                                <span>{result.matchStatus}</span>
                            </div>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Konečné skóre</span>
                                <span>{scoreOrDash(result.scoreHome, result.scoreAway)}</span>
                            </div>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Po 90 minutách</span>
                                <span>{scoreOrDash(result.regularTimeScoreHome, result.regularTimeScoreAway)}</span>
                            </div>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Po prodloužení</span>
                                <span>{scoreOrDash(result.extraTimeScoreHome, result.extraTimeScoreAway)}</span>
                            </div>
                            <div className="admin-score-row">
                                <span className="admin-score-label">Penalty</span>
                                <span>{scoreOrDash(result.penaltyScoreHome, result.penaltyScoreAway)}</span>
                            </div>
                        </div>

                        <ul className="admin-result-list">
                            <li>{result.pointsRecalculated ? '✅' : '⚠️'} Body uživatelských tipů přepočítány</li>
                            <li>{result.predictionPercentsUpdated ? '✅' : '⚠️'} Tipovací procenta aktualizována</li>
                            <li>{result.statisticsStored ? '✅' : '⚠️'} Statistiky staženy</li>
                            <li>{result.eventsStored ? '✅' : '⚠️'} Události staženy</li>
                        </ul>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UpdateMatchData;
